/**
 * ImportLeadsDialog - Modal de importação de leads com abas
 *
 * Abas:
 * 1. Importação: Upload de arquivo
 * 2. Configuração de Atribuição: Config temporária para aplicar durante importação
 *
 * Chrome = Modal shared (F72), abas = Tabs shared underline (F56),
 * dropzone = FileDropzone shared com botão explícito (F84),
 * footer = Button shared secondary/primary (F71).
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import type { DistributionConfig } from '@troia-v3/types';
import { AssignmentConfigSection } from '@/shared/components/assignment';
import { useFunnel } from '@/modules/funnels/hooks/useFunnels';
import { useLeadImport } from '../hooks/useLeadImport';
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Tabs,
  FileDropzone,
  Button,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

// Phase 1b: `funnel.assignmentConfig` is `DistributionConfig` direct.
// The import dialog optionally overrides this config for the batch —
// when the user picks a different strategy just for the imported leads.

interface ImportLeadsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (file: File, assignmentConfig?: DistributionConfig) => Promise<void>;
  stepName?: string;
  funnelId?: string;
}

type TabType = 'importacao' | 'configuracao';

export const ImportLeadsDialog: React.FC<ImportLeadsDialogProps> = ({
  isOpen,
  onClose,
  onImport,
  stepName,
  funnelId
}) => {
  // State
  const [activeTab, setActiveTab] = useState<TabType>('importacao');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [assignmentConfig, setAssignmentConfig] = useState<DistributionConfig | undefined>();

  // Hooks — fetch condicionado ao isOpen (regra de lazy fetching em modais)
  const { data: funnel, isLoading: isFunnelLoading } = useFunnel(isOpen ? funnelId : undefined);
  const { isImporting, message, percentage } = useLeadImport({
    onImportCompleted: () => {
      handleClose();
    }
  });

  // ✅ Inicializar assignmentConfig com dados do funil ou criar padrão
  useEffect(() => {
    if (funnel?.assignmentConfig) {
      setAssignmentConfig(funnel.assignmentConfig);
    } else if (funnel) {
      // Default: distribuição desativada (o usuário ativa explicitamente)
      setAssignmentConfig({
        enabled: false,
        type: 'sequential',
      });
    }
  }, [funnel]);

  // Handlers
  const handleClose = () => {
    if (isImporting) return;
    setSelectedFile(null);
    setActiveTab('importacao');
    onClose();
  };

  const handleFileSelect = (file: File) => {
    const validExtensions = ['.txt', '.csv', '.json'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      toast.error('Formato inválido. Use: .txt, .csv ou .json');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Tamanho máximo: 10MB');
      return;
    }

    setSelectedFile(file);
  };

  const handleImportClick = async () => {
    if (!selectedFile) return;

    try {
      await onImport(selectedFile, assignmentConfig);
      setSelectedFile(null);
    } catch (error) {
      console.error('Erro ao importar leads:', error);
    }
  };

  if (!isOpen) return null;

  const importTabContent = (
    <div className="space-y-6 pt-4">
      {/* File Upload Area — FileDropzone shared (F84) */}
      {selectedFile ? (
        <div
          className="border-2 border-dashed rounded-lg py-6 px-4 flex flex-col items-center gap-3 text-center"
          style={{ borderColor: 'var(--theme-input-border)' }}
          data-testid="import-leads-selected-file"
        >
          <Icon name="file" className="w-8 h-8" style={{ color: 'var(--theme-primary)' }} />
          <div>
            <p className="text-sm font-medium" style={{ color: '#1e293b' }}>
              {selectedFile.name}
            </p>
            <p className="text-xs mt-1" style={{ color: '#64748b' }}>
              {(selectedFile.size / 1024).toFixed(2)} KB
            </p>
          </div>
          {!isImporting && (
            <button
              type="button"
              onClick={() => setSelectedFile(null)}
              className="text-sm transition-colors"
              style={{ color: '#ef4444' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#dc2626'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#ef4444'; }}
              data-testid="import-leads-remove-file"
            >
              Remover arquivo
            </button>
          )}
        </div>
      ) : (
        <FileDropzone
          accept=".txt,.csv,.json"
          onFiles={(files) => { if (files[0]) handleFileSelect(files[0]); }}
          disabled={isImporting}
          showButton
          label="Arraste um arquivo ou clique para selecionar"
          hint="Formatos aceitos: .txt, .csv, .json (máx. 10MB)"
          buttonLabel="Selecionar Arquivo"
          data-testid="import-leads-dropzone"
        />
      )}

      {/* Progress Bar */}
      {isImporting && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span style={{ color: '#1e293b' }}>{message}</span>
            <span className="font-medium" style={{ color: 'var(--theme-primary)' }}>
              {percentage}%
            </span>
          </div>
          <div
            className="w-full rounded-full h-2 overflow-hidden"
            style={{ backgroundColor: '#e5e7eb' }}
          >
            <div
              className="h-full transition-all duration-300 ease-out"
              style={{
                backgroundColor: 'var(--theme-primary)',
                width: `${percentage}%`
              }}
            />
          </div>
        </div>
      )}

      {/* Info Box */}
      <div
        className="border rounded-lg p-4"
        style={{
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          borderColor: 'rgba(139, 92, 246, 0.3)'
        }}
      >
        <h4 className="text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
          ℹ️ Como funciona a importação com IA
        </h4>
        <ul className="text-xs space-y-1" style={{ color: '#64748b' }}>
          <li>• A IA extrai automaticamente: nome, email, telefone, descrição</li>
          <li>• Infere prioridade e temperatura baseado no contexto</li>
          <li>• Identifica se é B2B ou B2C</li>
          <li>• Detecta budget mencionado no texto</li>
        </ul>
      </div>
    </div>
  );

  const configTabContent = (
    <div className="pt-4">
      {isFunnelLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-sm" style={{ color: '#64748b' }}>
            Carregando configuração...
          </div>
        </div>
      ) : assignmentConfig ? (
        <AssignmentConfigSection
          config={assignmentConfig}
          onChange={(config) => setAssignmentConfig(config)}
          resourceType="funnel"
          {...(funnelId ? { resourceId: funnelId } : {})}
          variant="inline"
        />
      ) : (
        <div className="flex items-center justify-center py-12">
          <div className="text-sm" style={{ color: '#64748b' }}>
            Nenhuma configuração de atribuição encontrada
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="3xl">
      <ModalHeader title="Importar Leads" onClose={handleClose}>
        {stepName && (
          <span className="text-sm truncate" style={{ color: '#64748b' }}>
            Etapa: <span className="font-medium">{stepName}</span>
          </span>
        )}
      </ModalHeader>

      <ModalContent>
        {/* Abas — Tabs shared underline (F56); troca bloqueada durante a importação */}
        <Tabs
          variant="underline"
          activeTab={activeTab}
          onChange={(tabId) => {
            if (!isImporting) setActiveTab(tabId as TabType);
          }}
          tabs={[
            { id: 'importacao', label: 'Importação', icon: 'upload', content: importTabContent },
            { id: 'configuracao', label: 'Configuração de Atribuição', icon: 'settings', content: configTabContent },
          ]}
        />
      </ModalContent>

      <ModalFooter>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isImporting}
          data-testid="import-leads-cancel"
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleImportClick}
          disabled={!selectedFile || isImporting}
          data-testid="import-leads-confirm"
        >
          {isImporting ? 'Importando...' : 'Importar Leads'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
